import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ResultSearch from "../../components/VMRS/ExploreArchive/ResultSearch";
import SatelliteSearchPanel from "../../components/VMRS/ExploreArchive/SatelliteSearchPanel";
import { MapContext } from "../../components/VMRS/MapAdapter/core/MapContext";
import { UniversalMap } from "../../components/VMRS/MapAdapter/UniversalMap";
import { ETypeImage } from "../../enums";
import { showError } from "../../helpers/toast";
import ContentLayoutManagement from "../../layouts/ContentLayoutManagement";
import HeaderContent from "../../layouts/HeaderContent";
import
{
   getImageTypesAsync,
   searchAsync,
   getFormSearchByImageTypeAsync,
} from "../../store/reducers/mapTaskingSlice";
import
{
   resetProviderData,
   setDataImages,
   setDataImagesProvider,
   setLoadingProvider,
} from "../../store/reducers/searchSlice";
import { RootState } from "../../store/store";
import { Spin } from "antd";
import
{
   clearAOIFeatures,
   removeAOIFeature,
   setDrawingAOIMode,
   toggleFeatureVisibility,
} from "../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import
{
   checkAOIArea,
   getAOIAreaLimits,
   getSupportedAOITypes,
   getUnsupportedAOIFeatures,
} from "../../components/VMRS/ExploreTasking/ultis";
import
{
   clearAllLayers,
   removeLayer,
} from "../../components/VMRS/MapAdapter/store/slices/layerSlice";

import { clearFeatures as clearDrawFeatures, setDrawingMode } from "../../components/VMRS/MapAdapter/store/slices/drawSlice";
import { clearSegments as clearMeasureSegments, setMeasureMode } from "../../components/VMRS/MapAdapter/store/slices/measureSlice";

const ExploreArchivePage = () =>
{
   // const [mapInstance, setMapInstance] = useState<any>(null);

   const [loading, setLoading] = useState(false);
   const [engine, setEngine] = useState<any>(null);

   const [startDate, setStartDate] = useState<any>(dayjs().subtract(1, "month"));
   const [endDate, setEndDate] = useState<any>(dayjs());

   const [imageType, setImageType] = useState(ETypeImage.OPTICAL);

   const [data, setData] = useState<any[]>([]);
   const [showResultSearch, setShowResultSearch] = useState(false);
   const [showBackToResultSearch, setShowBackToResultSearch] = useState(false);

   const [providers, setProviders] = useState<any>([]);
   const [providerCode, setProviderCode] = useState(undefined);
   const [providerId, setProviderId] = useState(undefined);

   const providerCodeRef = useRef<string | undefined>(undefined);
   useEffect(() =>
   {
      providerCodeRef.current = providerCode;
   }, [providerCode]);

   const [imageTypes, setImageTypes] = useState<any>([]);

   const [formConfig, setFormConfig] = useState<any>(null);
   const [formData, setFormData] = useState<Record<string, any>>({});

   const depMap = useMemo(() =>
   {
      const options = formConfig?.ui_schema?.ui?.options || {};
      const map: Record<string, Array<{ fieldKey: string; expectedValue: string }>> = {};
      Object.entries(options).forEach(([key, opt]: [string, any]) =>
      {
         const switchField = opt.depends_on?.field ?? opt.depField;
         if (!switchField) return;
         const expectedValue = opt.depends_on?.value ?? key;
         map[switchField] = [...(map[switchField] ?? []), { fieldKey: key, expectedValue }];
      });
      return map;
   }, [formConfig]);

   const switchFieldValues = useMemo(() =>
   {
      return Object.keys(depMap)
         .map((k) => `${k}=${formData[k] ?? ""}`)
         .join("|");
   }, [depMap, formData]);

   const dispatch = useDispatch();
   const { t } = useTranslation();
   const allAOI = useSelector((state: RootState) => state.aoi.features);
   const activeFeatureId: any = useSelector(
      (state: RootState) => state.aoi.activeFeatureId,
   );

   let searchState = useSelector((state: RootState) => state.search);

   // The search form's geometry field (`ui_schema.ui.options.geometry`) says
   // which AOI shapes the image type accepts and the area bounds — the same
   // contract the tasking flow uses, wrapped as a one-step flow for its helpers.
   const geometryFlow = useMemo(
      () => (formConfig ? { steps: [{ ui: formConfig?.ui_schema?.ui }] } : null),
      [formConfig],
   );

   const declaredAOITypes = useMemo(
      () => getSupportedAOITypes(geometryFlow),
      [geometryFlow],
   );

   // Nothing declared → polygons only, as before.
   const supportedAOITypes = useMemo(
      () => declaredAOITypes ?? ["Polygon"],
      [declaredAOITypes],
   );

   // No bounds declared → no area check at all.
   const aoiAreaLimits = useMemo(
      () => getAOIAreaLimits(geometryFlow) ?? undefined,
      [geometryFlow],
   );

   // Only the set of shapes matters; the form config object is replaced on
   // every load and re-running on that would cancel a draw in progress.
   const declaredAOIKey = declaredAOITypes?.join("|") ?? "";

   useEffect(() =>
   {
      // The tool being drawn with may have just left the toolbar.
      dispatch(setDrawingAOIMode("NONE"));

      // Drop only the shapes the new image type can't take.
      getUnsupportedAOIFeatures(allAOI, declaredAOITypes).forEach((f) =>
         dispatch(removeAOIFeature(f.id)),
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [declaredAOIKey]);

   // Area message blocking the search when the active AOI is out of bounds.
   const aoiAreaError = useMemo(() =>
   {
      const activeAOI =
         (allAOI ?? []).find((feature: any) => feature.id === activeFeatureId) ??
         allAOI?.[0];
      const check = checkAOIArea(activeAOI, aoiAreaLimits ?? null);
      if (!check || check.status === "ok") return undefined;

      const area = check.areaKm2.toLocaleString(undefined, {
         maximumFractionDigits: check.areaKm2 < 10 ? 2 : 0,
      });
      return check.status === "too-large"
         ? t("Area {{area}} km² exceeds the {{max}} km² limit", {
            area,
            max: aoiAreaLimits?.maxKm2,
         })
         : t("Area {{area}} km² is below the {{min}} km² minimum", {
            area,
            min: aoiAreaLimits?.minKm2,
         });
   }, [allAOI, activeFeatureId, aoiAreaLimits, t]);

   let page = useSelector((state: RootState) => state.search.page);

   const layers = useSelector((state: RootState) => state.layer.layers);

   const handleSearch = () =>
   {
      setShowResultSearch(true);
      setShowBackToResultSearch(true);

      if (layers && layers.length > 0)
      {
         const targets = layers.filter(
            (layer) =>
               layer.id.startsWith("image-") || layer.id.startsWith("footerprint-"),
         );

         targets.forEach((layer) =>
         {
            dispatch(removeLayer(layer.id));
         });
      }

      if (!allAOI?.filter((aoi) => aoi.id === activeFeatureId)?.[0]?.visible)
      {
         dispatch(toggleFeatureVisibility({ id: activeFeatureId }));
      }

      searchAllProviders();
   };

   const searchAllProviders = async () =>
   {
      setData([]);
      dispatch(resetProviderData());
      if (providers?.length > 0)
      {
         const searchPromises = providers?.map(async (provider: any) =>
         {
            dispatch(setLoadingProvider({ code: provider.code, value: true }));

            // Build params dynamically from the form's dependency config (depMap):
            // - drop the UI-only switch (controller) fields, and
            // - keep only the dependent field that is active for the current
            //   controller value (drop the hidden ones).
            // This avoids hardcoding "resolution-swith".
            const formOptions = formConfig?.ui_schema?.ui?.options ?? {};
            const otherParams: any = { ...formData };

            Object.keys(depMap).forEach((controller) =>
            {
               const controllerValue =
                  formData[controller] ?? formOptions[controller]?.default;
               // The switch field itself is UI-only — never sent to BE.
               delete otherParams[controller];
               (depMap[controller] ?? []).forEach(({ fieldKey, expectedValue }) =>
               {
                  if (controllerValue !== expectedValue)
                  {
                     delete otherParams[fieldKey];
                  }
               });
            });

            const pay = {
               ...otherParams,
               aois: [allAOI?.[0]?.geometry],
               page_number: page,
               page_size: 20,
               provider_code: provider.code,
               image_type: imageType,
            };

            // Convert any date string fields to timestamps
            // Object.keys(pay).forEach((key) =>
            // {
            //    const value = pay[key];
            //    if (typeof value === "string" && value.length > 0)
            //    {
            //       const d = dayjs(value);
            //       if (d.isValid())
            //       {
            //          pay[key] = d.valueOf();
            //       }
            //    }
            // });

            Object.keys(pay).forEach((key) =>
            {
               const value = pay[key];

               if (typeof value === "string" && value.length > 0)
               {
                  let d = dayjs(value);
                  if (!d.isValid()) return;

                  const isToday = d.isSame(dayjs(), "day");

                  const isStart =
                     key.toLowerCase().includes("start") ||
                     key.toLowerCase().includes("from");
                  const isEnd =
                     key.toLowerCase().includes("end") ||
                     key.toLowerCase().includes("to");

                  if (isStart)
                  {
                     d = d.startOf("day"); // 00:00:00
                  }

                  if (isEnd)
                  {
                     if (isToday)
                     {
                        d = dayjs(); // giờ hiện tại
                     } else
                     {
                        d = d.endOf("day"); // 23:59:59
                     }
                  }

                  pay[key] = d.valueOf();
               }
            });

            try
            {
               // @ts-ignore
               const resSearch = await dispatch(searchAsync(pay)).unwrap();
               if (resSearch?.status !== 200)
               {
                  showError(t(resSearch?.response?.data?.message ?? "Error"));
               }



               // BE response: { message, data: { type, numberReturned, features, links } }
               const collection = resSearch?.data?.data;
               const features = collection?.features ?? [];
               const total = collection?.numberReturned ?? features.length;
               // Pagination is driven by the collection-level `next` link, which
               // carries its own { method, href, body } to fetch the next page.
               const links = collection?.links;
               const nextLink = links?.find((l: any) => l?.rel === "next");

               dispatch(
                  setDataImagesProvider({
                     code: provider.code,
                     data: features,
                     page: page,
                     nextLink: nextLink,
                     firstLink: undefined,
                     total: total,
                  }),
               );

               if (provider.code === providerCodeRef.current)
               {
                  dispatch(
                     setDataImages({
                        code: provider.code,
                        data: features,
                     }),
                  );

                  setData(features);
               }

               dispatch(setLoadingProvider({ code: provider.code, value: false }));

               return {
                  code: provider.code,
                  total: total,
                  listImages: features,
                  [`loading_${provider.code}`]: false,
               };
            } catch (error)
            {
               dispatch(setLoadingProvider({ code: provider.code, value: false }));
               return {
                  code: provider.code,
                  total: 0,
                  listImages: [],
                  [`loading_${provider.code}`]: false,
               };
            }
         });
         const results = await Promise.all(searchPromises);
      }
   };

   // Load the next page using the collection-level `next` link, which already
   // carries its own request config { rel, method, href, body }.
   const getDataInmagsByNextLink = async (nextLink: any) =>
   {
      if (!nextLink?.href) return;

      const targetProvider = providerCode;

      try
      {
         const res = await axios({
            method: nextLink?.method || "POST",
            url: nextLink.href,
            data: nextLink?.body,
         });

         const collection = res?.data?.data;
         const features = collection?.features || [];
         // @ts-ignore
         const baseForTarget = searchState[`data_images_${targetProvider}`] ?? [];
         const newArr = [...baseForTarget, ...features];
         const links = collection?.links;
         const next = links?.find((item: any) => item?.rel === "next");

         // Accumulate numberReturned across pages so the provider badge shows the
         // running total of images loaded so far.
         // @ts-ignore
         const prevTotal = searchState[`total_images_${targetProvider}`] ?? baseForTarget.length;
         const total = (prevTotal || 0) + (collection?.numberReturned ?? features.length);

         dispatch(
            setDataImages({
               code: targetProvider,
               data: newArr,
            }),
         );

         dispatch(
            setDataImagesProvider({
               code: targetProvider,
               data: newArr,
               page: 1,
               nextLink: next,
               firstLink: undefined,
               total: total,
            }),
         );

         if (providerCodeRef.current === targetProvider)
         {
            setData(newArr);
         }
      } catch (error)
      {
      } finally
      {
         dispatch(setLoadingProvider({ code: targetProvider, value: false }));
      }
   };

   // Called when the list reaches the end of locally-loaded items. Fetches the
   // next page from BE via the `next` link (method/href/body) if there is one.
   const handleScroll = () =>
   {
      // @ts-ignore
      const nextLink = searchState[`next_link_${providerCode}`];

      if (nextLink?.href)
      {
         dispatch(setLoadingProvider({ code: providerCode, value: true }));
         getDataInmagsByNextLink(nextLink);
      }
   };

   const handleGetImageTypes = async () =>
   {
      try
      {
         setLoading(true);
         // @ts-ignore
         let res = await dispatch(getImageTypesAsync()).unwrap();

         if (res?.status === 200)
         {
            // Only show image types that are active. Among the shown ones,
            // SatelliteSearchPanel enables the ones that have providers and
            // disables the rest.
            let dataImageTypes = (res?.data?.data ?? []).filter(
               (item: any) => item?.is_active,
            );

            setImageTypes(dataImageTypes);

            // Find first image type with providers
            const imageTypeWithProviders = dataImageTypes.find(
               (item: any) => item?.providers && item?.providers?.length > 0,
            );

            if (imageTypeWithProviders)
            {
               // Set the image type
               setImageType(imageTypeWithProviders.value);
               setProviders(imageTypeWithProviders.providers);
               setProviderCode(imageTypeWithProviders.providers[0]?.code);
               setProviderId(imageTypeWithProviders.providers[0]?.id);
            }
         }
      } catch (error)
      {
         console.error(error);
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      handleGetImageTypes();
   }, []);

   const handleResetMap = () =>
   {
      dispatch(clearAOIFeatures());
      dispatch(clearDrawFeatures());
      dispatch(clearMeasureSegments());
      dispatch(clearAllLayers());
      dispatch(setDrawingAOIMode("NONE"));
      dispatch(setDrawingMode("NONE"));
      dispatch(setMeasureMode("NONE"));
   };

   useEffect(() =>
   {
      handleResetMap();

      return () =>
      {
         handleResetMap();
      };
   }, []);

   // Load form configuration when image type changes
   useEffect(() =>
   {
      const loadFormConfig = async () =>
      {
         if (!imageTypes || imageTypes.length === 0)
         {
            return;
         }
         const currentImageType = imageTypes.find(
            (item: any) => item.value === imageType,
         );
         if (!currentImageType?.key)
         {
            console.warn("No form config key found for image type:", imageType);
            return;
         }
         try
         {
            setLoading(true);
            // @ts-ignore
            const formRes = await dispatch(
               // @ts-ignore
               getFormSearchByImageTypeAsync({ key: currentImageType.key }),
            ).unwrap();

            if (formRes?.status === 200)
            {
               setFormConfig(formRes?.data?.data);
               const defaults: Record<string, any> = {};

               // Extract defaults from ui_schema.options FIRST (including range defaults) - higher priority
               if (formRes?.data?.data?.ui_schema?.ui?.options)
               {
                  Object.entries(formRes.data.data.ui_schema.ui.options).forEach(
                     ([key, option]: [string, any]) =>
                     {
                        // Support both single default and range defaults (defaultMin/defaultMax)
                        if (
                           option.defaultMin !== undefined &&
                           option.defaultMax !== undefined
                        )
                        {
                           defaults[key] = [option.defaultMin, option.defaultMax];
                        } else if (option.defaultMin !== undefined)
                        {
                           defaults[key] = [option.defaultMin, option.defaultMin];
                        } else if (option.defaultMax !== undefined)
                        {
                           defaults[key] = [option.defaultMax, option.defaultMax];
                        } else if (option.default !== undefined)
                        {
                           defaults[key] = option.default;
                        }
                     },
                  );
               }

               // Handle dependencies: filter out fields whose depField condition is not met
               if (formRes?.data?.data?.ui_schema?.ui?.options)
               {
                  Object.entries(formRes.data.data.ui_schema.ui.options).forEach(
                     ([key, option]: [string, any]) =>
                     {


                        if (option.depField || option.depends_on)
                        {
                           const depField = option.depends_on?.field || option.depField;
                           const depValue = option.depends_on?.value ?? key;

                           if (!depField) return;

                           const controllerValue = defaults[depField];

                           if (
                              controllerValue !== undefined &&
                              controllerValue !== depValue
                           )
                           {
                              delete defaults[key];
                           }
                        }
                     },
                  );
               }



               const dateOptions =
                  formRes?.data?.data?.ui_schema?.ui?.options ?? {};
               const fromCfg = dateOptions.date_from;
               const toCfg = dateOptions.date_to;

               // Span between the two dates. Falls back to 1 month so the window
               // isn't zero-width when the schema doesn't declare max_range_days.
               const maxRangeDays =
                  toCfg?.max_range_days ?? fromCfg?.max_range_days;
               const today = dayjs();

               // Anchor the date that is closest to "now" on today, then derive the
               // other end so both stay inside the allowed window.
               // - future (tasking): start = today,  end = today + N
               // - past   (archive): end   = today,  start = today - N
               const restriction =
                  toCfg?.date_restriction ?? fromCfg?.date_restriction ?? "past";

               if (restriction === "future")
               {
                  if (fromCfg && defaults.date_from === undefined)
                  {
                     defaults.date_from = today.toISOString();
                  }
                  if (toCfg && defaults.date_to === undefined)
                  {
                     const start = defaults.date_from ? dayjs(defaults.date_from) : today;
                     defaults.date_to = (
                        maxRangeDays != null
                           ? start.add(maxRangeDays, "day")
                           : start.add(1, "month")
                     ).toISOString();
                  }
               }
               else
               {
                  if (toCfg && defaults.date_to === undefined)
                  {
                     defaults.date_to = today.toISOString();
                  }
                  if (fromCfg && defaults.date_from === undefined)
                  {
                     const end = defaults.date_to ? dayjs(defaults.date_to) : today;
                     defaults.date_from = (
                        maxRangeDays != null
                           ? end.subtract(maxRangeDays, "day")
                           : end.subtract(1, "month")
                     ).toISOString();
                  }
               }

               setFormData(defaults);
            }
         } catch (error)
         {
            console.error(
               "Failed to fetch form config for image type:",
               imageType,
               error,
            );
         } finally
         {
            setLoading(false);
         }
      };

      loadFormConfig();
   }, [imageType, imageTypes, dispatch, startDate, endDate]);

   // Dynamically resolve defaults for dep-field controlled fields when any switch field changes
   useEffect(() =>
   {
      if (!formConfig || Object.keys(depMap).length === 0) return;

      const options: Record<string, any> = formConfig.ui_schema?.ui?.options || {};

      const resolveDefault = (fieldOpts: any): any =>
      {
         if (fieldOpts?.default !== undefined) return fieldOpts.default;
         if (fieldOpts?.defaultMin !== undefined || fieldOpts?.defaultMax !== undefined)
         {
            return [fieldOpts.defaultMin ?? 0, fieldOpts.defaultMax ?? 100];
         }
         return undefined;
      };

      setFormData((prev) =>
      {
         const next = { ...prev };

         Object.entries(depMap).forEach(([switchField, deps]) =>
         {
            const switchValue = prev[switchField];

            deps.forEach(({ fieldKey }) => delete next[fieldKey]);

            if (switchValue !== undefined && switchValue !== null)
            {
               const active = deps.find((d) => d.expectedValue === switchValue);
               if (active)
               {
                  const defaultValue = resolveDefault(options[active.fieldKey]);
                  if (defaultValue !== undefined)
                  {
                     next[active.fieldKey] = defaultValue;
                  }
               }
            }
         });

         return next;
      });
   }, [switchFieldValues, formConfig]);

   useEffect(() =>
   {
      if (!providerCode || !layers || layers.length === 0) return;

      const targets = layers.filter(
         (layer) =>
            layer.id.startsWith("image-") || layer.id.startsWith("footerprint-"),
      );

      targets.forEach((layer) =>
      {
         dispatch(removeLayer(layer.id));
      });
   }, [providerCode]);

   return (
      <MapContext.Provider
         value={{
            engine: engine,
            pluginManager: null,
            mapType: "maplibre",
            mapInstance: engine?.map,
         }}
      >
         <ContentLayoutManagement
            header={
               <HeaderContent breadcrumb={[{ title: t("Explore Archive") }]} />
            }
         >
            <div className="h-full flex bg-[var(--neutral-1000)] text-[var(--color-main-text)]">
               {/* LEFT PANEL */}
               <div className="w-5/12 min-w-[380px] shrink-0 flex flex-col border-r border-[#26324a]">
                  <Spin spinning={loading}>
                     {!showResultSearch && (
                        <SatelliteSearchPanel
                           imageType={imageType}
                           setImageType={setImageType}
                           handleSearch={handleSearch}
                           showBackToResultSearch={showBackToResultSearch}
                           setShowResultSearch={setShowResultSearch}
                           providers={providers}
                           imageTypes={imageTypes}
                           aoiAreaError={aoiAreaError}
                           formConfig={formConfig}
                           formData={formData}
                           setFormData={setFormData}
                           setProviders={setProviders}
                           setProviderCode={setProviderCode}
                           setProviderId={setProviderId}
                        />
                     )}

                     {showResultSearch && (
                        <>
                           <ResultSearch
                              providerCode={providerCode}
                              setProviderCode={setProviderCode}
                              providers={providers}
                              data={data}
                              setShowResultSearch={setShowResultSearch}
                              setData={setData}
                              mapInstance={engine?.map}
                              handleScroll={handleScroll}
                              providerId={providerId}
                              loading={loading}
                              setLoading={setLoading}
                           />
                        </>
                     )}
                  </Spin>
               </div>

               {/* RIGHT MAP */}
               <div className="w-7/12 flex-1 min-w-0 relative">
                  <UniversalMap
                     mapType="maplibre"
                     showEngine={false}
                     showDraw={false}
                     showDrawAOI={!showResultSearch}
                     supportedAOITypes={supportedAOITypes}
                     aoiAreaLimits={aoiAreaLimits}
                     showMeasure={false}
                     view={{
                        center: [108.23653393802581, 16.1039225743402],
                        zoom: 5,
                     }}
                     showBaseMap={true}
                     showMapControl={true}
                     onMapReady={setEngine}
                     showSearchOSM={false}
                     allowMoveActive={!showResultSearch}
                  />
               </div>
            </div>
         </ContentLayoutManagement>
      </MapContext.Provider>
   );
};

export default ExploreArchivePage;
